import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import { DEFAULT_FEE_ATTACHMENT_BODY } from 'citadel-shared/variables.js'

import { replaceConfigVariables } from '../../util/variableFormatting'
import { billTypeMap } from '../../util/billingConsts'
import { FlexColumn, FlexRow, SpaceBetweenRowContainer } from '../styles/Containers'
import { BoldFormTitle, BoldMedBodyText, MedBodyText, Subheading } from '../styles/Text'
import AttachmentReadOnlyHeading from '../foiAttachments/AttachmentReadOnlyHeading'
import RichTextReadOnly from '../inputFields/RichTextReadOnly'
import AssignedUserChip from '../common/AssignedUserChip'
import SelectedAttachment from '../foiAttachments/SelectedAttachment'

const SentAtContainer = styled(FlexRow)`
  flex-wrap: wrap;
`

const SentByContainer = styled(FlexRow)`
  align-items: center;
`

function formatDate(date) {
  if (!date) return '—'
  return format(date, 'MMM-dd-yyyy hh:mm a')
}

function CommunicationReadOnly({ state, communication, publicConfig = {} }) {
  const { name, body, updatedAt, sentAt, sentBy, bill, letter } = communication

  const isDraft = state === 'DRAFT'
  const detailDate = isDraft ? updatedAt : sentAt
  const bodyHtml =
    !isDraft || (isDraft && !bill) ? body : `${body}<p></p>${replaceConfigVariables({ html: DEFAULT_FEE_ATTACHMENT_BODY, publicConfig })}`

  return (
    <FlexColumn gap="15px">
      {isDraft ? (
        <AttachmentReadOnlyHeading state="DRAFT_COMMUNICATION" attachment={communication} />
      ) : (
        <FlexColumn gap="0">
          <BoldFormTitle>{name}</BoldFormTitle>
          <SpaceBetweenRowContainer>
            <SentAtContainer gap="5px">
              <MedBodyText>Sent at:</MedBodyText>
              <BoldMedBodyText>{formatDate(detailDate)}</BoldMedBodyText>
            </SentAtContainer>
            <SentByContainer gap="5px">
              {sentBy && (
                <>
                  <MedBodyText>Sent by:</MedBodyText>
                  <AssignedUserChip firstName={sentBy.firstName} lastName={sentBy.lastName} color={sentBy.chipColor} />
                </>
              )}
            </SentByContainer>
          </SpaceBetweenRowContainer>
        </FlexColumn>
      )}
      <RichTextReadOnly value={bodyHtml} />
      {(letter || bill) && (
        <FlexColumn gap="10px">
          <Subheading>Attachments</Subheading>
          <FlexColumn gap="5px">
            {letter && <SelectedAttachment foiId={communication.foiId} attachmentType="LETTER" attachment={letter} showStatus={isDraft} />}
            {bill && (
              <SelectedAttachment
                foiId={communication.foiId}
                attachmentType="BILL"
                attachment={{ ...bill, name: `${bill.secondaryId} - ${billTypeMap[bill.type]}`, state: bill.billState, status: bill.workflowStatus }}
                showStatus={isDraft}
              />
            )}
          </FlexColumn>
        </FlexColumn>
      )}
    </FlexColumn>
  )
}

const userSummaryPropType = PropTypes.shape({
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  chipColor: PropTypes.string
})

CommunicationReadOnly.propTypes = {
  state: PropTypes.oneOf(['SENT', 'DRAFT']).isRequired,
  communication: PropTypes.shape({
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    updatedAt: PropTypes.instanceOf(Date).isRequired,
    modifiedBy: userSummaryPropType,
    sentAt: PropTypes.instanceOf(Date),
    sentBy: PropTypes.oneOfType([PropTypes.string, userSummaryPropType])
  }).isRequired,
  publicConfig: PropTypes.object
}

export default CommunicationReadOnly
